import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useLocale } from "lib/hooks/useLocale";
import { currencyFormatter } from "lib/utils/formatters";
import { getDeliveryInformation } from "lib/utils/merchant";
import { classNames } from "lib/utils/helpers";
import React, { useMemo } from "react";
import { Icon } from "@shopflo/ui/icon";
import { Check, HandCoins } from "@phosphor-icons/react";
import { usePaymentContext } from "lib/contexts/PaymentProvider";

interface HandleProps {
  id: string;
  name: string;
  charge: string;
  originalPrice?: string;
  isChecked: boolean;
  codEnabled: boolean;
  codExtraPrice: number;
  etdText?: string;
  onlineEnabled: boolean;
  minDays?: number;
  maxDays?: number;
}

export const ShippingHandle: React.FC<HandleProps> = React.memo(
  ({
    id,
    name,
    charge,
    originalPrice,
    isChecked,
    codEnabled,
    codExtraPrice,
    etdText,
    onlineEnabled,
    minDays,
    maxDays,
  }) => {
    const { t } = useLocale();
    const {
      state: { checkoutView, isC2P, appliedCoupons },
    } = useCheckoutContext();

    const {
      state: { paymentMethods },
    } = usePaymentContext();

    const {
      state: { merchant },
    } = useMerchantContext();

    const deliveryInformation = useMemo(
      () =>
        getDeliveryInformation(
          minDays ?? 0,
          maxDays ?? 0,
          etdText ?? "",
          merchant?.hideShippingMaxDays ?? false,
        ),
      [minDays, maxDays, etdText, merchant?.hideShippingMaxDays],
    );

    charge = parseFloat(charge)?.toFixed(2);
    const isShippingDiscountApplied = appliedCoupons?.some((coupon) => coupon.couponType === "SHIPPING");

    const { hasOnlySplitCODMode, hasCODModes } = useMemo(() => {
      const hasSplitCOD =
        paymentMethods?.some((paymentMethod) => paymentMethod?.name === "SPLIT_COD") ?? false;
      const hasCOD = paymentMethods?.some((paymentMethod) => paymentMethod?.name === "COD") ?? false;

      return {
        hasOnlySplitCODMode: hasSplitCOD && !hasCOD,
        hasCODModes: hasSplitCOD || hasCOD,
      };
    }, [paymentMethods]);

    const getCodTag = () => {
      if (!codEnabled) return <></>;

      const renderExtraCharges = () => {
        if ((hasOnlySplitCODMode && merchant?.splitCod?.excludeCodCharges) || !codExtraPrice) {
          return <></>;
        }

        return <span className="text-xs font-medium text-yay-dark"> {t("plus_extra_charges")}</span>;
      };

      if (!onlineEnabled) {
        return (
          <>
            <div className="flex items-end gap-1">
              <Icon IconComponent={HandCoins} size="xs" className="text-yay-dark" />
              <p className="text-xs font-medium text-yay-dark">
                {hasOnlySplitCODMode ? t("split_cod_only") : t("cod_only")}
              </p>
            </div>
          </>
        );
      }

      if (checkoutView === "PAYMENTS" && !isC2P) {
        return (
          <div className="flex gap-1 items-end">
            <Icon IconComponent={HandCoins} size="xs" className="text-yay-dark" />
            <p className="text-xs font-medium text-yay-dark">
              {hasOnlySplitCODMode ? t("split_cod_available") : t("cod_available")}
            </p>
            {renderExtraCharges()}
          </div>
        );
      }

      return <></>;
    };

    const renderPricing = () => {
      if (isShippingDiscountApplied) {
        if (originalPrice) {
          return (
            <div className="flex flex-col items-end gap-1">
              {parseFloat(charge) === 0 ? (
                <p className="text-sm font-medium uppercase text-yay-dark text-nowrap">
                  {t("free_shipping")}
                </p>
              ) : (
                <span className="text-sm font-medium text-yay-dark text-nowrap">
                  + {currencyFormatter(charge)}
                </span>
              )}

              <span className="text-xs font-normal text-zinc-400 line-through text-nowrap">
                {currencyFormatter(originalPrice, true)}
              </span>
            </div>
          );
        }
        return (
          <div className="flex flex-col items-end gap-1">
            <div className="text-xs font-medium text-yay-dark bg-yay-light py-0.5 px-1 rounded-sm">
              {t("reveal_offer")}
            </div>

            <span className="text-xs font-normal text-zinc-400 line-through">
              {currencyFormatter(charge, true)}
            </span>
          </div>
        );
      }

      if (parseFloat(charge) === 0) {
        return (
          <p className="text-sm font-medium uppercase text-yay-dark text-nowrap">{t("free_shipping")}</p>
        );
      }

      return (
        <p className="text-sm font-medium text-coal-dark flex-start text-nowrap">
          + {currencyFormatter(charge)}
        </p>
      );
    };

    const renderShippingDiscountAppliedTag = () => {
      if (!originalPrice) return <></>;

      return (
        <div className="flex items-end gap-1">
          <Icon IconComponent={Check} size="xxs" className="text-yay-dark" iconProps={{ weight: "bold" }} />
          <p className="text-yay-dark text-xs font-medium">{t("shipping_discount_applied")}</p>
        </div>
      );
    };

    return (
      <>
        <label
          className={classNames(
            "relative mx-3 flex h-full min-h-[4rem] cursor-pointer flex-row items-start justify-between rounded-2xl border px-3 py-3",
            isChecked ? "border-[1.5px] border-primary-dark px-[11.5px] py-[11.5px]" : "border-gray-light",
          )}>
          <span
            className={classNames(
              "flex h-4 w-4 min-w-[1rem] !items-center !justify-center rounded-full border-2",
              isChecked ? "border-primary-dark" : "border-gray-light",
            )}>
            <span
              className={classNames(
                "h-2 w-2 rounded-full",
                isChecked ? "bg-primary-dark" : "bg-none",
              )}></span>
          </span>
          <div className="flex w-full flex-col justify-center pl-3">
            <div className="flex w-full flex-row justify-between">
              <div className="flex flex-col gap-1">
                <p className=" text-sm text-coal-dark">{name}</p>
                {Boolean(deliveryInformation) && (
                  <p className="text-xs text-coal-light">{deliveryInformation}</p>
                )}
                {hasCODModes && getCodTag()}
                {renderShippingDiscountAppliedTag()}
              </div>
              {renderPricing()}
            </div>
          </div>
        </label>
      </>
    );
  },
);

export default ShippingHandle;
